import ApiEngine from "./ApiEngine.js";
import { ApiKey, ApiUrl } from "./Constant";

export const logConsoleErrorToLocalStorage = async (payload) => {
    // Generate or get a unique ID for this tab
    let TAB_ID = sessionStorage.getItem("TAB_ID");
    if (!TAB_ID) {
        TAB_ID = crypto.randomUUID(); // modern browsers
        sessionStorage.setItem("TAB_ID", TAB_ID);
    }

    const TAB_KEY = 'client_error_logs_' + TAB_ID;
    const MAX_ENTRIES = 100;    // Max occurrences
    const MAX_SIZE = 500 * 1024; // 500kb

    try {
        const now = new Date().toISOString();
        if (!payload.timestamp) payload.timestamp = now;

        // Trim stack/rawPayloadJson to save space
        payload.stack = payload.stack?.substring(0, 2000);
        // if (payload.rawPayloadJson) payload.rawPayloadJson = payload.rawPayloadJson.substring(0, 2000);

        // Load existing logs
        let logs = JSON.parse(localStorage.getItem(TAB_KEY) || '{"payloads":{},"occurrences":[]}');

        // Ensure logs object structure
        logs.payloads = logs.payloads || {};
        logs.occurrences = logs.occurrences || [];

        // Store unique payload if new
        if (!logs.payloads[payload.errorHash]) {
            logs.payloads[payload.errorHash] = {
                ...payload,
                firstSeen: now,
                lastSeen: now,
            };
        } else {
            logs.payloads[payload.errorHash].lastSeen = now;
        }

        // Add occurrence
        logs.occurrences.push({ errorHash: payload.errorHash, timestamp: now });

        logs = trimLogs(logs, MAX_ENTRIES, MAX_SIZE);

        // Save back
        localStorage.setItem(TAB_KEY, JSON.stringify(logs));

    } catch (err) {
        console.warn("Failed to log error to localStorage:", err);
    }
}

const trimLogs = (logs, maxEntries, maxSize, maxAgeMs = null) => {
    // Default values for reference
    // const MAX_ENTRIES = 100;    // Max occurrences
    // const MAX_SIZE = 1024 * 1024; // 1MB
    // const MAX_AGE_MS = 1 * 60 * 60 * 1000; // 1 hour

    const now = Date.now();

    // Remove old entries
    if (maxAgeMs) {
        logs.occurrences = logs.occurrences.filter(
            entry => (now - new Date(entry.timestamp || entry._timestamp).getTime()) <= maxAgeMs
        );
    }

    // Trim to max entries
    if (logs.occurrences.length > maxEntries) {
        logs.occurrences = logs.occurrences.slice(-maxEntries);
    }

    // Trim by approximate size
    let size = new Blob([JSON.stringify(logs)]).size;
    // console.log('size:', size);
    while (size > maxSize && logs.occurrences.length > 0) {
        // console.log('trimming...');
        // console.log('size:', size);
        logs.occurrences.shift();
        size = new Blob([JSON.stringify(logs)]).size;
    }

    return logs;
};

const FLUSH_MAX_ATTEMPTS = 5;
/** Wait after 1st failed attempt; later waits use {@link FLUSH_RETRY_MAX_MS}. */
const FLUSH_RETRY_FIRST_MS = 60_000;
/** Cap between retries (60s first gap, then 120s). */
const FLUSH_RETRY_MAX_MS = 120_000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** POST merged logs; retries transient failures (throws, non-success body). Returns true only when server reports success. */
const postMergedClientErrorLogs = async (mergedLogs) => {
    let lastFailure;
    for (let attempt = 1; attempt <= FLUSH_MAX_ATTEMPTS; attempt++) {
        try {
            const response = await ApiEngine.post(ApiUrl._API_LOG_JS_ERROR, mergedLogs);
            if (response && response[ApiKey._API_SUCCESS_KEY] === true) {
                return true;
            }
            lastFailure = response?.message || response;
        } catch (err) {
            lastFailure = err;
        }
        if (attempt < FLUSH_MAX_ATTEMPTS) {
            const waitMs = attempt === 1 ? FLUSH_RETRY_FIRST_MS : FLUSH_RETRY_MAX_MS;
            await sleep(waitMs);
        }
    }
    console.warn(
        `Failed to send merged JS errors after ${FLUSH_MAX_ATTEMPTS} attempts:`,
        lastFailure
    );
    return false;
};

let mergeAndFlushInFlight = null;

const mergeAndFlushClientErrorLogsOnce = async () => {
    const MAX_ENTRIES = 100;    // Max occurrences
    const MAX_SIZE = 500 * 1024; // 500kb

    try {
        // Get all localStorage keys that start with 'client_error_logs_'
        const tabKeys = Object.keys(localStorage).filter(k => k.startsWith('client_error_logs_'));

        let mergedLogs = {
            payloads: {},
            occurrences: []
        };

        // Merge all tab logs
        for (const key of tabKeys) {
            const raw = localStorage.getItem(key);
            if (!raw) continue;

            const tabLog = JSON.parse(raw);
            if (!tabLog.payloads || !tabLog.occurrences) continue;

            // Merge payloads
            for (const [hash, payload] of Object.entries(tabLog.payloads)) {
                if (!mergedLogs.payloads[hash]) {
                    mergedLogs.payloads[hash] = payload;
                } else {
                    // Update firstSeen/lastSeen if needed
                    mergedLogs.payloads[hash].firstSeen = 
                        mergedLogs.payloads[hash].firstSeen < payload.firstSeen 
                        ? mergedLogs.payloads[hash].firstSeen 
                        : payload.firstSeen;
                    mergedLogs.payloads[hash].lastSeen = 
                        mergedLogs.payloads[hash].lastSeen > payload.lastSeen 
                        ? mergedLogs.payloads[hash].lastSeen 
                        : payload.lastSeen;
                }
            }

            // Merge occurrences
            mergedLogs.occurrences.push(...tabLog.occurrences);
        }

        // Sort occurrences by timestamp
        mergedLogs.occurrences.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

        // // Optional: trim occurrences to MAX_ENTRIES
        // const MAX_ENTRIES = 1000;
        // if (mergedLogs.occurrences.length > MAX_ENTRIES) {
        //     mergedLogs.occurrences = mergedLogs.occurrences.slice(-MAX_ENTRIES);
        // }

        if (!mergedLogs.occurrences.length) return;

        //console.log('%c[LOGGER] Sending client error logs', 'background: blue;');

        mergedLogs = trimLogs(mergedLogs, MAX_ENTRIES, MAX_SIZE);

        const ok = await postMergedClientErrorLogs(mergedLogs);
        if (!ok) return;

        tabKeys.forEach(k => localStorage.removeItem(k));

    } catch (err) {
        console.warn("Failed to merge and flush client error logs:", err);
    }
};

export const mergeAndFlushClientErrorLogs = async () => {
    if (mergeAndFlushInFlight) {
        return mergeAndFlushInFlight;
    }
    mergeAndFlushInFlight = (async () => {
        try {
            await mergeAndFlushClientErrorLogsOnce();
        } finally {
            mergeAndFlushInFlight = null;
        }
    })();
    return mergeAndFlushInFlight;
};

// CHRIS TODO 20251211: Temporarily disable API Call logging first, need to plan how to save these data
// export const logApiCallToLocalStorage = async ({ url, method, status, requestBody, responseBody }) => {
//     const payload = {
//         url,
//         method,
//         status,
//         requestBody: requestBody ? JSON.stringify(requestBody).slice(0, 500) : null,
//         responseBody: responseBody ? JSON.stringify(responseBody).slice(0, 500) : null,
//         timestamp: new Date().toISOString()
//     };

//     try {
//         const KEY = 'client_api_logs';
//         const MAX_ENTRIES = 100;
//         let logs = JSON.parse(localStorage.getItem(KEY) || '[]');
//         logs.push(payload);
//         if (logs.length > MAX_ENTRIES) logs = logs.slice(-MAX_ENTRIES);
//         localStorage.setItem(KEY, JSON.stringify(logs));
//     } catch (err) {
//         console.warn("Failed to log API call:", err);
//     }
// };